import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { SidebarPropertyDynamicPricingComponent } from '@app/modules/calendar/components/sidebar-property-dynamic-pricing/sidebar-property-dynamic-pricing.component';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { UiModule } from '@app/ui';
import { BadgeType } from '@app/ui/badge-mono/badge.component.interface';
import { EventsService } from '@app/shared/services/events/events.service';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { DynamicPricingOptionsResponse } from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { filter, takeUntil } from 'rxjs';
import {
  DynamicPricingService,
  PropertyAvailabilityRulesFormComponent,
  StepTriggerComponent,
  PropertyBasePriceComponent,
  PropertyPricingService,
  PropertyListingMarkupsComponent,
  PropertyBookingRatePlansComponent,
  PropertyFeesTaxesDiscountsComponent,
} from '@app/features/pricing-and-availability';

enum View {
  DYNAMIC_PRICING = 'dynamic-pricing',
  LISTING_MARKUPS = 'listing-markups',
  PRIMARY_RATE_PLAN = 'primary-rate-plan',
  SECURITY_DEPOSIT = 'security-deposit',
  OCCUPANCY_BASED = 'occupancy-based',
  FEES = 'fees',
  DISCOUNTS = 'discounts',
}

/**
 * TODO:
 * - Enforce correct checks for rate plan visibility
 * - Enforce correct checks for listing markups visibility
 * - consolidate data fetches in service in order to calculate sublines
 * - Adjust styling for container
 */

@Component({
  standalone: true,
  selector: 'sbnb-sidebar-property-settings',
  templateUrl: './sidebar-property-settings.component.html',
  styles: `
    @import 'variables';
    .sticky-footer {
      margin: auto;
      width: 100%;
      border-top: 1px solid $smoke;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      background: $white;
      position: sticky;
      bottom: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PropertyAvailabilityRulesFormComponent,
    CommonModule,
    UiModule,
    SidebarPropertyDynamicPricingComponent,
    StepTriggerComponent,
    PropertyBasePriceComponent,
    StepTriggerComponent,
    PropertyListingMarkupsComponent,
    PropertyBookingRatePlansComponent,
    PropertyFeesTaxesDiscountsComponent,
  ],
  providers: [PropertyPricingService],
})
export class SidebarPropertySettingsComponent extends OnDestroyComponent implements OnInit {
  public currentView: View | null = null;
  @Input() property: any;
  @Output() refreshCalendar = new EventEmitter<void>();

  public badgeType = BadgeType;
  public viewEnum = View;
  public $dynamicPricingOptions = this.dynamicPricingService.$config;
  public $dynamicPricingOn = this.dynamicPricingService.$dynamicPricingOn;

  constructor(
    private eventsService: EventsService,
    private propertiesService: PropertiesService,
    private cdr: ChangeDetectorRef,
    private dynamicPricingService: DynamicPricingService,
    private propertyPricingService: PropertyPricingService
  ) {
    super();
  }

  ngOnInit() {
    this.eventsService
      .on(`dynamic-pricing-disabled-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.propertiesService.getDynamicPricingOptions(this.property.id).subscribe((data) => {
          this.updateViewData(data as DynamicPricingOptionsResponse);
        });
      });

    this.eventsService
      .on(`dynamic-pricing-enabled-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateViewData();
      });

    this.eventsService
      .on(`dynamic-pricing-sidebar-view-force-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((view?: View) => {
        this.setView(view);
      });

    this.eventsService
      .on<boolean>(`dynamic-pricing-visible-change-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visible) => {
        if (!visible) {
          this.setView();
        }
      });

    this.propertyPricingService.saved$
      .pipe(
        filter((saved) => Boolean(saved)),
        takeUntil(this.destroyed$)
      )
      .subscribe((saved) => {
        this.refreshCalendar.emit();
      });
  }

  updateViewData(data?: DynamicPricingOptionsResponse) {
    if (data) {
      this.dynamicPricingService.setDynamicPricingConfig(data);
    }
    this.setView();
  }

  public setView(view?: View | null) {
    this.currentView = view ?? null;
    this.cdr.detectChanges();
  }

  public setViewDynamicPricing() {
    const { in_market, data_errors } = this.$dynamicPricingOptions();
    if (!in_market || data_errors) {
      this.eventsService.fire(`dynamic-pricing-property-error-modal-${this.property.id}`, true);
    } else {
      this.currentView = this.viewEnum.DYNAMIC_PRICING;
      this.cdr.detectChanges();
    }
  }

  public goBack() {
    this.setView();
  }
}
